import { Field, FieldAttributes, useField, useFormikContext } from 'formik'
import { TextField } from 'formik-mui'
import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { string } from 'yup'

import { Field as CBField, FieldQueryOptions, FieldsAPI } from '@cibo/core'
import { logRequestError } from '@cibo/ui'
import { CircularProgress, TextFieldProps } from '@mui/material'
import { useEffect } from 'react'

export const MAX_LENGTH_NAME = 255

/**
 * For use with forms that have an owner field
 */
export const fieldNameValidationSchemaWithOwnerField = ({
  excludeResourceId,
  fieldName,
  t,
}: {
  excludeResourceId?: CBField['resourceId']
  fieldName: FieldAttributes<string>['name']
  t: TFunction
}) =>
  string()
    .required(t('required', { context: 'name', ns: '@cibo/landmanager/NameEditor' }))
    .max(
      MAX_LENGTH_NAME,
      t('max', { context: 'name', max: MAX_LENGTH_NAME, ns: '@cibo/landmanager/NameEditor' })
    )
    .test(
      fieldName,
      t('unique', { context: 'name', ns: '@cibo/landmanager/NameEditor' }),
      async function (name) {
        if (!!this.parent.owner) {
          return FieldsAPI.nameAvailable({
            excludeResourceId,
            name: `${name}`,
            owner: this.parent.owner,
          })
            .then(available => available)
            .catch(e => {
              logRequestError(e)
              // return is required for validation
              return false
            })
        }
        return true
      }
    )

/**
 * For use with forms that DO NOT have an owner field.
 */
export const fieldNameValidationSchemaByUserId = ({
  excludeResourceId,
  fieldName,
  t,
  userId,
}: {
  excludeResourceId?: CBField['resourceId']
  fieldName: FieldAttributes<string>['name']
  t: TFunction
  userId: FieldQueryOptions['owner']
}) =>
  string()
    .required(t('required', { context: 'name', ns: '@cibo/landmanager/NameEditor' }))
    .max(
      MAX_LENGTH_NAME,
      t('max', { context: 'name', max: MAX_LENGTH_NAME, ns: '@cibo/landmanager/NameEditor' })
    )
    .test(
      fieldName,
      t('unique', { context: 'name', ns: '@cibo/landmanager/NameEditor' }),
      async function (name) {
        return FieldsAPI.nameAvailable({ excludeResourceId, name: `${name}`, owner: userId })
          .then(available => available)
          .catch(e => {
            logRequestError(e)
            // return is required for validation
            return false
          })
      }
    )

export const NameEditorField = ({
  name,
  ...textFieldProps
}: Pick<FieldAttributes<string>, 'name'> & TextFieldProps) => {
  const { t } = useTranslation('@cibo/landmanager/NameEditor')
  const [{ value }] = useField(name)
  const { isValidating, setTouched } = useFormikContext()

  // touch the field on mount if there is an initial value to ensure
  // that validation will render an immediate error message
  useEffect(() => {
    if (!!value) {
      setTouched({ [name]: true })
    }
  }, [])

  return (
    <Field
      {...textFieldProps}
      name={name}
      component={TextField}
      InputProps={{
        //type: search *should* disable most password managers that pick up on this field due to its name
        inputProps: { 'data-testid': name, autocomplete: 'off', ...textFieldProps.inputProps },
        InputProps: {
          endAdornment: isValidating ? <CircularProgress size="small" /> : null,
          ...textFieldProps.InputProps,
        },
      }}
      maxLength={MAX_LENGTH_NAME}
      placeholder={t('placeholder')}
      fullWidth={true}
    />
  )
}
